import { useEffect, useRef, useState } from "react";
import { Check, Flame, Hand, Loader2, Mail, MapPin, MessageCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";
import { ORIGIN, pathFor } from "../lib/routing";
import { submitReservation } from "../lib/booking";
import { FlagArgentina, FlagUSA } from "../components/PlaceFlags";
import { WHATSAPP_NUMBER } from "../lib/reviews";
import { GalleryCarousel, InstagramShowcase } from "./PalermoExperience";

// Fotos del curso (originales en /curso, optimizadas a WebP en public/curso).
const HERO = "/curso/hero.webp";
const MODULE_IMAGES = [
  "/curso/modulo-01-fuego.webp",
  "/curso/modulo-02-parrilla.webp",
  "/curso/modulo-03-chimichurri.webp",
  "/curso/modulo-04-cortes.webp",
  "/curso/modulo-05-tiempos.webp",
  "/curso/modulo-06-servicio.webp",
];
const GALLERY = ["/curso/galeria-01.webp", "/curso/galeria-02.webp", "/curso/galeria-03.webp", "/curso/galeria-04.webp"];

const MAX_GUESTS = 10;
// Mismo precio en las dos sedes; si cambia, tocar también scripts/seo-prerender.js.
const PRICE_PER_GUEST = 200;

const PLACES = [
  { id: "argentina", Flag: FlagArgentina },
  { id: "miami", Flag: FlagUSA },
];

/**
 * Formulario de reserva del curso, con pestaña de sede (Argentina / Miami).
 * Dos vías, igual que el formulario de la cena: WhatsApp (el pedido viaja en el
 * chat) o email, que va a api/reserva.php con type: "curso" — ahí la sede
 * reemplaza al horario y el precio es el del curso.
 */
function CursoBookingForm() {
  const { t, lang } = useI18n();
  const c = t.curso;
  const [place, setPlace] = useState("argentina");
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 1, date: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const openedAt = useRef(Date.now());
  const todayISO = new Date().toISOString().slice(0, 10);
  const current = c.form_places[place];
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const setGuests = (n) => setForm({ ...form, guests: Math.min(MAX_GUESTS, Math.max(1, n)) });

  // El email es opcional para WhatsApp y obligatorio para el envío por mail,
  // que es a donde va el acuse de recibo.
  const validate = ({ requireEmail }) => {
    if (!form.name.trim()) {
      toast.error(c.form_name_required);
      return false;
    }
    if (requireEmail && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error(t.booking.email_required);
      return false;
    }
    return true;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate({ requireEmail: false })) return;
    const prettyDate = form.date
      ? new Date(form.date + "T12:00:00").toLocaleDateString(lang === "en" ? "en-US" : "es-AR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
      : null;
    const lines = [
      c.wa_greeting,
      "",
      `${c.form_place_wa}: ${current.wa}`,
      `${t.booking.name}: ${form.name.trim()}`,
      `${t.booking.guests}: ${form.guests}`,
      prettyDate ? `${t.booking.date}: ${prettyDate}` : null,
      form.phone.trim() ? `${t.booking.phone}: ${form.phone.trim()}` : null,
      form.email.trim() ? `${t.booking.email}: ${form.email.trim()}` : null,
      form.message.trim() ? `${c.form_message.replace(/ \(.*\)$/, "")}: ${form.message.trim()}` : null,
      "",
      `${t.booking.total}: USD ${form.guests * PRICE_PER_GUEST}`,
    ].filter((line) => line !== null);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
  };

  const submitEmail = async () => {
    if (sending || !validate({ requireEmail: true })) return;
    setSending(true);
    try {
      const result = await submitReservation(
        {
          type: "curso",
          place,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          guests: form.guests,
          date: form.date,
          notes: form.message.trim(),
          lang,
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        },
        { lang }
      );
      setSent({ ...result, email: form.email.trim() });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSent(null);
    openedAt.current = Date.now();
    setForm({ name: "", email: "", phone: "", guests: 1, date: "", message: "", website: "" });
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 text-[#F5F3F0] px-0 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-[#555] [color-scheme:dark]";
  const stepperBtnCls = "shrink-0 w-11 h-11 flex items-center justify-center border border-white/20 text-[#F5F3F0] text-2xl leading-none select-none transition-colors hover:border-ember hover:text-ember disabled:opacity-30 disabled:pointer-events-none";

  if (sent) {
    return (
      <div className="border border-ember/40 bg-ember/[0.04] p-8 md:p-12" data-testid="curso-sent">
        <span className="w-12 h-12 flex items-center justify-center border border-ember text-ember"><Check size={22} /></span>
        <h3 className="font-display font-light text-3xl md:text-4xl text-[#F5F3F0] mt-6">{t.booking.sent_title}</h3>
        <p className="mt-4 text-[#A3A3A3] leading-relaxed">
          {(sent.guestEmailed ? t.booking.sent_body : t.booking.sent_no_email).replace("__EMAIL__", sent.email)}
        </p>
        {sent.guestEmailed && <p className="mt-3 text-sm text-[#777] leading-relaxed">{t.booking.sent_spam}</p>}
        {sent.ref && (
          <div className="mt-8 border-t border-white/10 pt-6 flex items-baseline gap-4">
            <span className="overline">{t.booking.sent_ref}</span>
            <span className="font-display text-2xl text-ember tracking-wider">{sent.ref}</span>
          </div>
        )}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
          <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="btn-primary justify-center">
            {t.booking.sent_whatsapp}
          </a>
          <button type="button" onClick={reset} className="btn-ghost justify-center">{t.booking.sent_again}</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="curso-form">
      <div>
        <span className="overline block mb-3" id="curso-place-label">{c.form_place_label}</span>
        <div className="grid grid-cols-2 gap-3" role="tablist" aria-labelledby="curso-place-label">
          {PLACES.map(({ id, Flag }) => {
            const active = place === id;
            return (
              <button key={id} type="button" role="tab" aria-selected={active} onClick={() => setPlace(id)}
                data-testid={`curso-tab-${id}`}
                className={`flex items-center gap-3 md:gap-4 border px-3 md:px-5 py-4 text-left transition-colors ${active ? "border-ember bg-ember/10" : "border-white/10 hover:border-ember/50"}`}>
                <Flag className={`w-9 md:w-11 shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-opacity ${active ? "" : "opacity-60"}`} />
                <span className="min-w-0">
                  <span className={`block font-display text-lg md:text-xl leading-tight ${active ? "text-ember" : "text-[#F5F3F0]"}`}>{c.form_places[id].tab}</span>
                  <span className="block text-[11px] text-[#A3A3A3] mt-1">{c.form_places[id].place}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <label className="block">
          <span className="overline block mb-2">{t.booking.name}</span>
          <input required value={form.name} onChange={set("name")} placeholder={lang === "en" ? "John Doe" : "Juan Pérez"} className={inputCls} data-testid="curso-name-input" />
        </label>
        <label className="block">
          <span className="overline block mb-2">{t.booking.email}</span>
          <input type="email" value={form.email} onChange={set("email")} placeholder="email@example.com" className={inputCls} />
        </label>
        <label className="block">
          <span className="overline block mb-2">{t.booking.phone}</span>
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder={current.phone} className={inputCls} />
        </label>
        <div className="block">
          <span className="overline block mb-2">{t.booking.guests}</span>
          <div className="flex items-center gap-3 border-b border-white/20">
            <button type="button" onClick={() => setGuests(form.guests - 1)} disabled={form.guests <= 1} aria-label={lang === "en" ? "Fewer people" : "Menos personas"} className={stepperBtnCls}>−</button>
            <span className="flex-1 text-center text-lg py-3 text-[#F5F3F0]" aria-live="polite">{form.guests}</span>
            <button type="button" onClick={() => setGuests(form.guests + 1)} disabled={form.guests >= MAX_GUESTS} aria-label={lang === "en" ? "More people" : "Más personas"} className={stepperBtnCls}>+</button>
          </div>
        </div>
      </div>

      <label className="block">
        <span className="overline block mb-2">{c.form_date}</span>
        <input type="date" min={todayISO} value={form.date} onChange={set("date")} className={inputCls} />
      </label>

      <label className="block">
        <span className="overline block mb-2">{c.form_message}</span>
        <textarea rows={3} value={form.message} onChange={set("message")} placeholder={c.form_message_placeholder} className={inputCls} />
      </label>

      <div className="flex items-baseline justify-between border-t border-white/10 pt-5">
        <span className="overline">{t.booking.total}</span>
        <span className="font-display text-3xl text-ember" data-testid="curso-total">USD {form.guests * PRICE_PER_GUEST}</span>
      </div>

      {/* Trampa para bots: fuera de la pantalla, sin tab y oculta a lectores. */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
        value={form.website} onChange={set("website")}
        className="absolute left-[-9999px] top-0 w-px h-px opacity-0 pointer-events-none" />

      <div className="pt-2">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <button type="submit" disabled={sending} className="btn-primary justify-center" data-testid="curso-submit">
            <Flame size={14} /> {c.form_submit}
          </button>
          <button type="button" onClick={submitEmail} disabled={sending} className="btn-secondary justify-center" data-testid="curso-email-btn">
            {sending
              ? <><Loader2 size={14} className="animate-spin" /> {t.booking.sending}</>
              : <><Mail size={14} /> {t.booking.submit_email}</>}
          </button>
        </div>
        <p className="mt-4 text-xs text-[#A3A3A3] leading-relaxed">{c.form_note}</p>
        <p className="mt-2 text-xs text-[#A3A3A3] leading-relaxed">{c.form_email_note}</p>
      </div>
    </form>
  );
}

/**
 * Title, description y JSON-LD (Course) de la ruta. El canonical y los hreflang los
 * maneja RouteSeo a nivel global; acá no se tocan. Mismo patrón que PalermoExperience.
 */
function SeoMetadata() {
  const { t, lang } = useI18n();

  useEffect(() => {
    const title = t.curso.seo_title;
    const description = t.curso.seo_description;
    const canonical = `${ORIGIN}${pathFor("curso", lang)}`;
    const previousTitle = document.title;
    const previousMeta = new Map();
    document.title = title;

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (element && !previousMeta.has(selector)) previousMeta.set(selector, element.getAttribute("content"));
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, selector.match(/\[(?:name|property)='([^']+)'\]/)?.[1]);
        previousMeta.set(selector, null);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };

    setMeta("meta[name='description']", "name", description);
    setMeta("meta[property='og:title']", "property", title);
    setMeta("meta[property='og:description']", "property", description);
    setMeta("meta[property='og:url']", "property", canonical);
    setMeta("meta[name='twitter:title']", "name", title);
    setMeta("meta[name='twitter:description']", "name", description);
    setMeta("meta[property='og:image']", "property", `${ORIGIN}/og-curso.jpg`);
    setMeta("meta[name='twitter:image']", "name", `${ORIGIN}/og-curso.jpg`);

    // Mismo objeto que courseSchema() en scripts/seo-prerender.js.
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Course",
      name: title,
      description,
      url: canonical,
      inLanguage: lang,
      image: [`${ORIGIN}/og-curso.jpg`],
      teaches: t.curso.learn_items.map(([, name]) => name),
      provider: { "@type": "Organization", name: "Asado Xperience", sameAs: ORIGIN },
      offers: { "@type": "Offer", price: String(PRICE_PER_GUEST), priceCurrency: "USD", availability: "https://schema.org/InStock", url: canonical },
      hasCourseInstance: [
        { locality: "Palermo Soho", region: "Buenos Aires", country: "AR" },
        { locality: "Miami", region: "FL", country: "US" },
      ].map((a) => ({
        "@type": "CourseInstance",
        courseMode: "Onsite",
        instructor: { "@type": "Person", name: "Diego" },
        location: {
          "@type": "Place",
          name: "Asado Xperience",
          address: { "@type": "PostalAddress", addressLocality: a.locality, addressRegion: a.region, addressCountry: a.country },
        },
      })),
    };

    document.querySelectorAll("script[data-route-schema]").forEach((el) => el.remove());
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.routeSchema = "true";
    schema.textContent = JSON.stringify(structuredData);
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      previousMeta.forEach((content, selector) => {
        const element = document.head.querySelector(selector);
        if (!element) return;
        if (content === null) element.remove();
        else element.setAttribute("content", content);
      });
      document.querySelector("script[data-route-schema]")?.remove();
    };
  }, [t, lang]);

  return null;
}

export default function CursoParrillero() {
  const { t } = useI18n();
  const c = t.curso;

  return (
    <>
      <SeoMetadata />
      <main>
        <section className="relative min-h-[78vh] flex items-end overflow-hidden" aria-labelledby="curso-title">
          <img src={HERO} alt={c.hero_alt} fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charred via-charred/75 to-charred/25" />
          <div className="absolute inset-0 grain" />
          <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-20 pt-40">
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{c.hero_overline}</span>
            <h1 id="curso-title" className="font-display font-light tracking-tight text-[#F5F3F0] mt-6 text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] max-w-5xl">{c.hero_title_1}<br /><em className="italic text-ember">{c.hero_title_2}</em></h1>
            <p className="max-w-2xl mt-8 text-base md:text-lg text-[#D1CEC9] leading-relaxed">{c.hero_subtitle}</p>
            <a href="#reservar" className="btn-primary mt-10 inline-flex" data-testid="curso-hero-cta"><Flame size={14} /> {c.hero_cta}</a>
          </div>
        </section>

        <section className="py-24 md:py-36" aria-labelledby="curso-intro-title">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
            <div className="md:col-span-7">
              <span className="overline flex items-center gap-3"><span className="divider-gold" />{c.intro_overline}</span>
              <h2 id="curso-intro-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{c.intro_title}</h2>
              <p className="mt-8 text-[#F5F3F0] text-lg md:text-xl leading-relaxed">{c.intro_p1}</p>
              <p className="mt-5 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{c.intro_p2}</p>
              <p className="mt-5 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{c.intro_p3}</p>
            </div>
            <div className="md:col-span-5"><GalleryCarousel images={GALLERY} alts={c.gallery_alts} /></div>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5" aria-labelledby="curso-learn-title">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{c.learn_overline}</span>
            <h2 id="curso-learn-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{c.learn_title}</h2>
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-16 mt-16">
              {c.learn_items.map(([number, title, body], index) => (
                <article key={number}>
                  <div className="overflow-hidden aspect-[4/3]">
                    <img src={MODULE_IMAGES[index]} alt={c.learn_alts[index]} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <span className="block border-t border-white/10 pt-5 mt-6" />
                  <span className="font-display text-4xl text-ember italic">{number}</span>
                  <h3 className="font-display text-3xl text-[#F5F3F0] mt-3">{title}</h3>
                  <p className="text-[#A3A3A3] mt-3 leading-relaxed">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 md:py-36" aria-labelledby="curso-details-title">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <span className="overline flex items-center gap-3"><span className="divider-gold" />{c.details_overline}</span>
              <h2 id="curso-details-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{c.details_title}</h2>
              <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{c.details_body}</p>
            </div>
            <div className="space-y-5 md:pt-12">
              <div className="flex gap-4 border-t border-white/10 pt-5"><MapPin className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{c.details_location}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><Hand className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{c.details_format}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><Users className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{c.details_capacity}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><MessageCircle className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{c.details_price}</p></div>
            </div>
          </div>
        </section>

        <InstagramShowcase />

        <section id="reservar" className="py-24 md:py-36 relative overflow-hidden scroll-mt-20" aria-labelledby="curso-cta-title">
          <div className="absolute inset-0 bg-gradient-to-b from-charred via-[#0d0a07] to-charred" />
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-5 md:sticky md:top-32 h-fit">
              <span className="overline flex items-center gap-3"><span className="divider-gold" />{c.cta_overline}</span>
              <h2 id="curso-cta-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{c.cta_title}</h2>
              <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">{c.cta_body}</p>
              <div className="mt-10 border-t border-white/10 pt-8">
                <span className="overline">{c.form_price_overline}</span>
                <p className="font-display text-4xl text-ember mt-3">{c.form_price_per_guest}</p>
                <p className="text-sm text-[#A3A3A3] mt-3">{c.form_price_note}</p>
              </div>
            </div>
            <div className="md:col-span-7"><CursoBookingForm /></div>
          </div>
        </section>
      </main>
    </>
  );
}
